import { useState } from 'react'
import { ColumnDef } from '@tanstack/react-table'
import { ArrowLeft, Download, Database, AlertCircle } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { Link, useNavigate } from 'react-router-dom'
import {
  useImportableAssistants,
  useImportAssistant,
  ImportableAssistant,
} from '@/hooks/useAssistants'
import { DataTable } from '@/components/common/DataTable'
import { useToast } from '@/hooks/use-toast'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip'

export function AssistantImport() {
  const [page, setPage] = useState(1)
  const [pageSize, setPageSize] = useState(50)
  const [search, setSearch] = useState('')

  const { data, isLoading } = useImportableAssistants({ page, pageSize, search })
  const importMutation = useImportAssistant()
  const { toast } = useToast()
  const navigate = useNavigate()

  const [importDialogOpen, setImportDialogOpen] = useState(false)
  const [selectedAssistant, setSelectedAssistant] = useState<ImportableAssistant | null>(null)
  const [importName, setImportName] = useState('')

  const handleImportClick = (assistant: ImportableAssistant) => {
    setSelectedAssistant(assistant)
    setImportName(assistant.name)
    setImportDialogOpen(true)
  }

  const handleImportConfirm = () => {
    if (!selectedAssistant) return

    importMutation.mutate(
      {
        ragflow_chat_id: selectedAssistant.id,
        name: importName.trim() || undefined,
      },
      {
        onSuccess: (res) => {
          const kbCount = res.imported_kbs?.length || 0
          let msg = `Imported "${res.assistant.name}"`
          if (kbCount > 0) {
            msg += ` with ${kbCount} auto-imported KB(s)`
          }
          toast({ title: 'Import successful', description: msg })
          setImportDialogOpen(false)
          setSelectedAssistant(null)
          const urlId = res.assistant.shared_id || res.assistant.id
          navigate(`/app/assistants/${urlId}`)
        },
        onError: (err: any) => {
          const message = err?.response?.data?.error || err.message || 'Import failed'
          toast({ title: 'Import failed', description: message, variant: 'destructive' })
        },
      }
    )
  }

  const columns: ColumnDef<ImportableAssistant, any>[] = [
    {
      accessorKey: 'name',
      header: 'Assistant Name',
      cell: ({ row }) => (
        <span className="font-medium">{row.original.name}</span>
      ),
    },
    {
      accessorKey: 'description',
      header: 'Description',
      cell: ({ row }) => (
        <span className="text-muted-foreground line-clamp-1 max-w-[250px]">
          {row.original.description || '—'}
        </span>
      ),
    },
    {
      id: 'datasets',
      header: 'Knowledge Bases',
      cell: ({ row }) => {
        const { datasets, missing_kb_count } = row.original
        if (!datasets || datasets.length === 0) {
          return <span className="text-muted-foreground">None</span>
        }
        return (
          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger asChild>
                <div className="flex items-center gap-1.5">
                  <Database className="h-3.5 w-3.5 text-muted-foreground" />
                  <span>{datasets.length}</span>
                  {missing_kb_count > 0 && (
                    <Badge variant="outline" className="text-xs text-amber-600 border-amber-300">
                      +{missing_kb_count} new
                    </Badge>
                  )}
                </div>
              </TooltipTrigger>
              <TooltipContent side="bottom" className="max-w-xs">
                <ul className="text-xs space-y-0.5">
                  {datasets.map((ds) => (
                    <li key={ds.id} className="flex items-center gap-1">
                      {ds.exists ? (
                        <Badge variant="default" className="text-[10px] px-1 py-0">existing</Badge>
                      ) : (
                        <Badge variant="outline" className="text-[10px] px-1 py-0 text-amber-600 border-amber-300">auto-import</Badge>
                      )}
                      <span>{ds.name}</span>
                    </li>
                  ))}
                </ul>
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
        )
      },
    },
    {
      id: 'actions',
      header: '',
      cell: ({ row }) => (
        <Button
          size="sm"
          onClick={() => handleImportClick(row.original)}
          disabled={importMutation.isPending}
        >
          <Download className="h-4 w-4 mr-1" />
          Import
        </Button>
      ),
    },
  ]

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <div className="flex items-center gap-3 mb-1">
            <Button variant="ghost" size="icon" asChild>
              <Link to="/app/assistants">
                <ArrowLeft className="h-4 w-4" />
              </Link>
            </Button>
            <h1 className="text-3xl font-bold">Import Assistants from RAGflow</h1>
          </div>
          <p className="text-muted-foreground ml-12">
            Import existing RAGflow chat assistants. Missing knowledge bases will be auto-imported.
          </p>
        </div>
      </div>

      <DataTable
        columns={columns}
        data={data?.items ?? []}
        total={data?.total ?? 0}
        page={page}
        pageSize={pageSize}
        search={search}
        isLoading={isLoading}
        onPageChange={setPage}
        onPageSizeChange={setPageSize}
        onSearchChange={setSearch}
        getRowId={(row) => row.id}
        emptyState={
          <div className="text-center py-12">
            <Download className="mx-auto h-12 w-12 text-muted-foreground/50" />
            <h3 className="mt-4 text-lg font-semibold">No assistants to import</h3>
            <p className="text-muted-foreground mt-2">
              All RAGflow chat assistants are already imported, or there are none available.
            </p>
            <Button asChild className="mt-4" variant="outline">
              <Link to="/app/assistants">Back to Assistants</Link>
            </Button>
          </div>
        }
      />

      {/* Import Dialog */}
      <Dialog open={importDialogOpen} onOpenChange={setImportDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Import Assistant</DialogTitle>
            <DialogDescription>
              Import this RAGflow chat assistant into Chatterpillar. You can optionally rename it.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-2">
            <div className="space-y-2">
              <Label htmlFor="import-name">Assistant Name</Label>
              <Input
                id="import-name"
                value={importName}
                onChange={(e) => setImportName(e.target.value)}
                placeholder="Enter a name for the assistant"
              />
            </div>

            {selectedAssistant && (
              <div className="rounded-md bg-muted p-3 text-sm space-y-1">
                <div>
                  <span className="font-medium">RAGflow ID:</span>{' '}
                  <code className="text-xs">{selectedAssistant.id}</code>
                </div>
                <div>
                  <span className="font-medium">Knowledge Bases:</span>{' '}
                  {selectedAssistant.dataset_count}
                  {selectedAssistant.missing_kb_count > 0 && (
                    <span className="text-amber-600 ml-1">
                      ({selectedAssistant.missing_kb_count} will be auto-imported)
                    </span>
                  )}
                </div>
              </div>
            )}

            {selectedAssistant && selectedAssistant.missing_kb_count > 0 && (
              <div className="flex items-start gap-2 rounded-md border border-amber-200 bg-amber-50 p-3 text-sm">
                <AlertCircle className="h-4 w-4 text-amber-600 mt-0.5 shrink-0" />
                <span className="text-amber-800">
                  {selectedAssistant.missing_kb_count} knowledge base(s) referenced by this assistant
                  don't exist in Chatterpillar yet and will be automatically imported.
                </span>
              </div>
            )}
          </div>

          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setImportDialogOpen(false)}
              disabled={importMutation.isPending}
            >
              Cancel
            </Button>
            <Button
              onClick={handleImportConfirm}
              disabled={importMutation.isPending || !importName.trim()}
            >
              {importMutation.isPending ? 'Importing...' : 'Import'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  )
}
